import moment from 'moment';
import React, { ReactElement, ReactNode } from 'react';
import ReactDOMServer from 'react-dom/server';
import { useHistory, useLocation } from 'react-router-dom';
import { format } from 'date-fns';
import { Timestamp } from '../components/Input/DatePicker/types';
import {
  SupportedXBCurrencies,
  SupportedXBCountries,
  RecipientClientAccountIDSplit,
  SupportedCurrencies,
  PseudoBool,
} from '../components/Utility/VoPayApi/VoPayApi.types';
import { LS_MFA_HASH, LS_REMEMBER_MFA } from '../constants';
export { default as acronym } from './acronym/acronym';

export const isPlainObject = (item: unknown): boolean => Object.prototype.toString.call(item) === '[object Object]';

export const isEmptyObject = (object: Record<string, unknown>): boolean =>
  object && Object.keys(object).length === 0 && object.constructor === Object;

export const isEmptyArray = (array: unknown): boolean => Array.isArray(array) && array.length === 0;

export const formatAsCurrency = (num: string, currencySymbol?: string): string => {
  const symbol = currencySymbol === undefined ? '$' : currencySymbol;

  return `${symbol}${num.replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,')}`;
};

export function arrayToObject<T>(array: T[]): { [key: string]: T } {
  return array.reduce((acc, item, index) => ({ ...acc, [index.toString()]: item }), {});
}

export const capitalizeFirstLetter = (str: string): string => str.charAt(0).toUpperCase() + str.slice(1);

export const capitalizeFirstLetterOfWords = (inputString: string): string =>
  inputString.replace(/\b\w/g, (char) => char.toUpperCase());

export const sanitizeToNumeric = (num?: string): string | undefined => (num ? num.replace(/,/g, '') : num);

export const sanatizeURL = (url: string): string => {
  if (url.indexOf('http://') !== 0 && url.indexOf('https://') !== 0) {
    return `https://${url}`;
  }
  return url;
};
export const getMaskedNumber = (num: string, mask?: string) => {
  let defaultMask = '***';
  if (mask) {
    defaultMask = mask;
  }
  const lastFourDigits = num ? num.slice(-4) : '';
  return `${defaultMask}${lastFourDigits}`;
};
// Renders a masked numeric account identifier — a backend length-preserving
// mask like '******1234' or a masked card number — as a uniform '***' + last-4
// display mask, so mixed sources don't show mixed star counts. Values that
// aren't numeric masks (email or bill-pay identifiers like 'j***@gmail.com',
// or a fully-starred mask with no digits) pass through untouched.
export const formatMaskedAccount = (accountNumber: string) => {
  const digits = accountNumber.replace(/\D/g, '');
  if (!digits || !/^[\d*\s-]+$/.test(accountNumber)) {
    return accountNumber;
  }
  return getMaskedNumber(digits);
};
export const padWithZero = (number: string, length: number) => {
  const numberString = String(number);
  const paddingLength = length - numberString.length;

  if (paddingLength <= 0) {
    return numberString; // No padding needed
  }

  const padding = '0'.repeat(paddingLength);
  return padding + numberString;
};

export const extractTextFromJSX = (element: ReactNode): string => {
  if (typeof element === 'string') {
    return element;
  }

  if (React.isValidElement(element)) {
    if (typeof element.type === 'string') {
      const node = element as ReactElement<any>;

      return React.Children.toArray(node.props.children)
        .map((child) => extractTextFromJSX(child))
        .join('');
    }

    const htmlString = ReactDOMServer.renderToStaticMarkup(element);
    const text = htmlString.replace(/<[^>]*>/g, '');
    return text.trim();
  }

  return '';
};

// export const extractTextFromBadgeJSX = extractTextFromJSX;
export const extractTextFromBadgeJSX = (element: React.ReactNode): string => {
  if (typeof element === 'string') {
    return element;
  }

  if (React.isValidElement(element)) {
    const node = element as ReactElement<any>;

    return React.Children.toArray(node.props.children.props.label)
      .map((child) => extractTextFromJSX(child))
      .join('');
  }

  return '';
};
export type General = {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export const removeEmptyProperty = <T>(object: General): T =>
  Object.fromEntries(Object.entries(object).filter(([, v]) => !([null, undefined, ''] as unknown[]).includes(v))) as T;

export const parseJWT = (token: string): JSON => {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join(''),
  );

  return JSON.parse(jsonPayload) as JSON;
};

export const downloadBlob = ({
  data,
  fileName,
  fileType,
}: {
  data: BlobPart;
  fileName: string;
  fileType: string;
}): void => {
  const blob = new Blob([data], { type: fileType });

  const clickEvent = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  });

  const a = document.createElement('a');
  const url = window.URL.createObjectURL(blob);
  a.download = fileName;
  a.href = url;
  a.dispatchEvent(clickEvent);
  a.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadBase64DataUri = ({
  data,
  fileName,
  fileType,
}: {
  data: string;
  fileName: string;
  fileType: string;
}): void => {
  const clickEvent = new MouseEvent('click', {
    view: window,
    bubbles: true,
    cancelable: true,
  });

  const a = document.createElement('a');

  a.download = fileName;
  a.href = `data:${fileType};base64,${data}`;
  a.dispatchEvent(clickEvent);
  a.remove();
};

export const downloadBase64AsFile = (base64Content: string, fileName: string): void => {
  // Create a blob from the base64 content
  const byteCharacters = atob(base64Content);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'application/octet-stream' });

  // Create download link and trigger download
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  window.URL.revokeObjectURL(url);
};

export const toBool = (val?: string): boolean => Boolean(parseInt(val || '', 2));

export const convertBase64 = (element: HTMLInputElement): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const file = element.files?.[0];
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    return file ? reader.readAsDataURL(file) : reject(new Error('No Files'));
  });

export const convertFileToBase64 = (file: File): Promise<string | ArrayBuffer | null> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      resolve(reader.result);
    };
    reader.onerror = () => {
      reject(new Error('Error reading the file.'));
    };
    reader.readAsDataURL(file);
  });

const validBankImages = [
  '1',
  '10',
  '100',
  '101',
  '10129',
  '102',
  '10314',
  '10394',
  '104',
  '105',
  '107',
  '108',
  '110-bw',
  '110',
  '111',
  '112',
  '113',
  '114',
  '115',
  '116',
  '117',
  '11731',
  '118',
  '119',
  '12',
  '120-bw',
  '120',
  '121',
  '122-bw',
  '122',
  '123',
  '124',
  '125',
  '126',
  '127',
  '128-bw',
  '128',
  '129',
  '13',
  '130-bw',
  '130',
  '131',
  '132',
  '133',
  '134',
  '135',
  '136-bw',
  '136',
  '137',
  '138',
  '139',
  '14',
  '140',
  '141-bw',
  '141',
  '1411',
  '1412',
  '1413',
  '1414',
  '1415',
  '1416',
  '1417-bw',
  '1417',
  '1418',
  '1419',
  '142',
  '1420-bw',
  '1420',
  '1422',
  '1423',
  '1424',
  '1425',
  '1426-bw',
  '1426',
  '1427',
  '1428',
  '1429',
  '143',
  '1430',
  '1431',
  '1432',
  '1433-bw',
  '1433',
  '1434',
  '1436',
  '1437',
  '1438',
  '1439',
  '144',
  '1440',
  '1441',
  '1442',
  '1443',
  '1445',
  '1446',
  '1447',
  '1448',
  '1449',
  '145-bw',
  '145',
  '1450',
  '1451',
  '1452',
  '1453',
  '1454',
  '1457',
  '1458',
  '1459',
  '146',
  '1460',
  '1461-bw',
  '1461',
  '1462',
  '1463',
  '1464',
  '1465',
  '1466',
  '1469',
  '147-bw',
  '147',
  '1470',
  '1471',
  '1472',
  '1473',
  '1475-bw',
  '1475',
  '1476',
  '1477',
  '1479',
  '148',
  '1480',
  '1483',
  '1484',
  '1485',
  '1486-bw',
  '1486',
  '1487-bw',
  '1487',
  '1488-bw',
  '1488',
  '1489-bw',
  '1489',
  '149',
  '1490',
  '1491',
  '1492',
  '1493-bw',
  '1493',
  '1494',
  '1495',
  '1496',
  '1498-bw',
  '1498',
  '1499',
  '15',
  '150-bw',
  '150',
  '1501',
  '1502',
  '1503',
  '1506',
  '1507',
  '1508',
  '1509',
  '151',
  '1512-bw',
  '1512',
  '1513',
  '1514',
  '1515',
  '1516-bw',
  '1516',
  '1517-bw',
  '1517',
  '1518',
  '1519',
  '152',
  '1520',
  '1522-bw',
  '1522',
  '1523',
  '1524',
  '1525',
  '15258',
  '1526',
  '1527',
  '1528',
  '1529',
  '153-bw',
  '153',
  '1530',
  '1531',
  '1533',
  '1534',
  '1536',
  '1537',
  '1538-bw',
  '1538',
  '1539',
  '154',
  '1542',
  '1543',
  '1544',
  '155',
  '15569',
  '156',
  '1569',
  '1570',
  '1571',
  '1572',
  '1573',
  '1574',
  '1575',
  '1576',
  '1577',
  '1579',
  '158',
  '1580',
  '1581',
  '1582',
  '1583',
  '1584',
  '1585',
  '1586',
  '1587',
  '1588',
  '1589',
  '159-bw',
  '159',
  '1590',
  '1591',
  '1592',
  '1593',
  '1594-bw',
  '1594',
  '1595',
  '1596',
  '1597',
  '1598',
  '1599',
  '16',
  '160',
  '1600',
  '1601',
  '1602',
  '1603',
  '1604',
  '1605',
  '1606',
  '1608',
  '1609',
  '1610',
  '1611',
  '1612',
  '1613',
  '1614',
  '1615',
  '1616',
  '1617',
  '1618',
  '1619',
  '162',
  '1620',
  '1623',
  '1625',
  '1626',
  '1627',
  '1628',
  '1629',
  '163-bw',
  '163',
  '1630',
  '1631',
  '1632',
  '1633',
  '1636',
  '1637',
  '1639',
  '1642',
  '1644',
  '1646',
  '1653',
  '1655',
  '1662',
  '1663',
  '1678',
  '1680',
  '17',
  '1730',
  '1731',
  '1732',
  '1733',
  '1734',
  '1735',
  '1736',
  '1737',
  '1738',
  '1739',
  '1740',
  '1741',
  '1742',
  '1743',
  '1744',
  '1745',
  '1746',
  '1748',
  '1749',
  '1750',
  '1751',
  '1752',
  '1753',
  '1754',
  '1755-bw',
  '1755',
  '1756',
  '1757',
  '1758-bw',
  '1758',
  '1759',
  '1760',
  '1761',
  '1762',
  '1763',
  '1764',
  '1765',
  '1766',
  '1767',
  '1768',
  '1769',
  '1770',
  '1771',
  '1772',
  '1773',
  '1774',
  '1775',
  '1776',
  '1777',
  '1778',
  '1779',
  '1780',
  '1781',
  '1782',
  '1783',
  '1784',
  '1785',
  '1787-bw',
  '1787',
  '1788',
  '1789',
  '179',
  '1790',
  '1791',
  '1793',
  '1794',
  '1795',
  '1796',
  '1797',
  '1799',
  '18',
  '1800',
  '1801',
  '1804',
  '1805',
  '1806-bw',
  '1806',
  '1807',
  '1808',
  '1809',
  '1810',
  '1812',
  '1813',
  '1814',
  '1817',
  '1818-bw',
  '1818',
  '1820',
  '1821',
  '1822',
  '1835',
  '1837',
  '1838',
  '1839',
  '1840',
  '1841',
  '1842',
  '1843',
  '1845',
  '1846',
  '1847',
  '1849',
  '1850',
  '1851',
  '1852',
  '1853-bw',
  '1853',
  '1855',
  '1856',
  '1857',
  '1858',
  '1859',
  '1860',
  '1862',
  '1863',
  '1864-bw',
  '1864',
  '1865',
  '1866-bw',
  '1866',
  '1867',
  '1869',
  '1873',
  '1875',
  '1877',
  '1882',
  '1883',
  '1884',
  '1885',
  '1886',
  '1887',
  '1888',
  '1889',
  '1890',
  '1891',
  '1888',
  '1893',
  '1894',
  '1896',
  '1897',
  '19',
  '1901',
  '1902',
  '1904',
  '1906',
  '1907',
  '1910',
  '1911',
  '1912',
  '1913',
  '1914',
  '1915',
  '1917',
  '1924',
  '1925',
  '1939',
  '1942',
  '1958',
  '1990',
  '2',
  '20',
  '2005',
  '2019',
  '2045',
  '2067',
  '2083',
  '21-bw',
  '21',
  '2166',
  '2168',
  '2172',
  '22',
  '2200',
  '2221',
  '2229',
  '2243',
  '2290',
  '2297',
  '23',
  '2306',
  '2312',
  '2317',
  '2349',
  '2355',
  '2361',
  '2369',
  '2377',
  '24',
  '2432',
  '2436',
  '2461',
  '2492',
  '25',
  '2534',
  '2578',
  '2597',
  '26',
  '2601',
  '2603',
  '2608',
  '2637',
  '2692',
  '27',
  '2711',
  '2717',
  '2719',
  '2767',
  '28',
  '2832',
  '2854',
  '2876',
  '29-bw',
  '29',
  '3-bw',
  '3',
  '30',
  '3012',
  '3025',
  '3032',
  '3055',
  '3066',
  '31',
  '32-bw',
  '32',
  '33-bw',
  '33',
  '34-bw',
  '34',
  '35-bw',
  '35',
  '36',
  '37',
  '38',
  '3853',
  '39',
  '4',
  '40',
  '41-bw',
  '41',
  '4247',
  '43',
  '4309',
  '4319',
  '44-bw',
  '44',
  '45-bw',
  '45',
  '46',
  '4654',
  '47-bw',
  '47',
  '4781',
  '4792',
  '4793',
  '4799',
  '48',
  '4801',
  '4804',
  '4802',
  '49',
  '5',
  '50',
  '5000',
  '502',
  '51',
  '52-bw',
  '52',
  '5219',
  '5275',
  '53',
  '54-bw',
  '54',
  '5420',
  '55',
  '5597',
  '56',
  '57-bw',
  '57',
  '58',
  '6',
  '60',
  '61',
  '62',
  '6236',
  '63',
  '64-bw',
  '64',
  '6490',
  '65',
  '66-bw',
  '66',
  '67',
  '6783',
  '68',
  '69',
  '7',
  '70',
  '71-bw',
  '71',
  '72',
  '73',
  '74',
  '75',
  '7541',
  '76',
  '78',
  '79',
  '8',
  '80',
  '81',
  '8226',
  '83',
  '84',
  '85',
  '86-bw',
  '86',
  '87',
  '88',
  '89',
  '8996',
  '9-bw',
  '9',
  '90',
  '91-bw',
  '91',
  '92-bw',
  '92',
  '93',
  '94',
  '96',
  '97',
  '9733',
  '98',
  '99',
  '15164',
  '82',
  '621',
  '1048',
  '1408',
  '1624',
  '1692',
  '1729',
  '1833',
  'back',
];
const canadaBanks = [
  {
    name: 'Airline Financial Credit Union Limited',
    code: '830',
  },
  {
    name: 'Alberta Treasury Branches',
    code: '219',
  },
  {
    name: 'All Trans Financial Services Credit Union Limited',
    code: '810',
  },
  {
    name: "Alliance des caisses populaires de l'Ontario Limitée",
    code: '890',
  },
  {
    name: 'Alterna Savings and Credit Union',
    code: '842',
  },
  {
    name: 'Amex Bank of Canada',
    code: '303',
  },
  {
    name: 'Arnstein Community Credit Union Limited',
    code: '808',
  },
  {
    name: 'Atlantic Central',
    code: '839',
  },
  {
    name: 'B2B Bank (formerly B2B Trust)',
    code: '618',
  },
  {
    name: 'Bank of America National Association',
    code: '241',
  },
  {
    name: 'Bank of Canada',
    code: '177',
  },
  {
    name: 'Bank of China (Canada)',
    code: '308',
  },
  {
    name: 'Bank of Montreal',
    code: '1',
  },
  {
    name: 'Bank of Tokyo-Mitsubishi UFJ (Canada)',
    code: '245',
  },
  {
    name: 'Bank West',
    code: '342',
  },
  {
    name: "Barclay's Bank PLC Canada Branch",
    code: '360',
  },
  {
    name: 'BNP Paribas (Canada)',
    code: '250',
  },
  {
    name: 'BofA Canada Bank',
    code: '311',
  },
  {
    name: 'Bridgewater Bank',
    code: '347',
  },
  {
    name: 'La Caisse Centrale Desjardins Du Québec',
    code: '815',
  },
  {
    name: 'Canadian Imperial Bank of Commerce',
    code: '10',
  },
  {
    name: 'Canadian Tire Bank',
    code: '338',
  },
  {
    name: 'Canadian Western Bank',
    code: '30',
  },
  {
    name: 'Capital One Bank (Canada Branch)',
    code: '323',
  },
  {
    name: 'Central 1 Credit Union British Columbia',
    code: '809',
  },
  {
    name: 'Central 1 Credit Union Ontario',
    code: '828',
  },
  {
    name: 'CIBC Trust Corporation',
    code: '548',
  },
  {
    name: 'Citibank Canada',
    code: '260',
  },
  {
    name: 'Citibank N.A.',
    code: '328',
  },
  {
    name: 'Citizens Bank of Canada',
    code: '309',
  },
  {
    name: 'Comerica Bank',
    code: '330',
  },
  {
    name: 'Communication Technologies Credit Union Limited',
    code: '807',
  },
  {
    name: 'Community Trust Company',
    code: '507',
  },
  {
    name: 'Concentra Financial Services Association',
    code: '853',
  },
  {
    name: 'Credit Union Central Alberta Limited',
    code: '899',
  },
  {
    name: 'Credit Union Central of Manitoba Limited',
    code: '879',
  },
  {
    name: 'Credit Union Central of Saskatchewan',
    code: '889',
  },
  {
    name: 'CS Alterna Bank',
    code: '608',
  },
  {
    name: 'CTC Bank of Canada',
    code: '315',
  },
  {
    name: 'Deutsche Bank AG',
    code: '265',
  },
  {
    name: 'DirectCash Bank',
    code: '352',
  },
  {
    name: 'Dundalk District Credit Union Limited',
    code: '840',
  },
  {
    name: 'Dundee Bank of Canada',
    code: '343',
  },
  {
    name: 'Effort Trust Company',
    code: '532',
  },
  {
    name: 'Fifth Third Bank',
    code: '345',
  },
  {
    name: 'First Commercial Bank',
    code: '332',
  },
  {
    name: 'First Nations Bank of Canada',
    code: '310',
  },
  {
    name: 'La Fédération des caisses populaires Acadiennes Limitée',
    code: '865',
  },
  {
    name: "La Fédération des caisses populaires de l'Ontario Inc.",
    code: '829',
  },
  {
    name: 'La Fédération des caisses populaires du Manitoba Inc.',
    code: '819',
  },
  {
    name: 'General Bank of Canada',
    code: '344',
  },
  {
    name: 'Goderich Community Credit Union Limited',
    code: '844',
  },
  {
    name: 'Golden Horseshoe Credit Union Limited',
    code: '854',
  },
  {
    name: 'Habib Canadian Bank',
    code: '321',
  },
  {
    name: 'HomEquity Bank',
    code: '358',
  },
  {
    name: 'Household Trust Company',
    code: '630',
  },
  {
    name: 'HSBC Bank Canada',
    code: '16',
  },
  {
    name: 'HSBC Bank USA National Association',
    code: '333',
  },
  {
    name: 'HSBC Mortgage Corporation (Canada)',
    code: '604',
  },
  {
    name: 'ICICI Bank Canada',
    code: '340',
  },
  {
    name: 'Industrial Alliance Trust Inc.',
    code: '625',
  },
  {
    name: 'Industrial and Commercial Bank of China (Canada)',
    code: '307',
  },
  {
    name: 'ING Bank of Canada',
    code: '614',
  },
  {
    name: 'Investors Group Trust Co. Ltd.',
    code: '536',
  },
  {
    name: 'J.P. Morgan Bank Canada',
    code: '314',
  },
  {
    name: 'Jameson Bank',
    code: '354',
  },
  {
    name: 'JPMorgan Chase Bank National Association',
    code: '270',
  },
  {
    name: 'Korea Exchange Bank of Canada',
    code: '275',
  },
  {
    name: 'Latvian Credit Union Limited',
    code: '803',
  },
  {
    name: 'Laurentian Bank of Canada',
    code: '39',
  },
  {
    name: 'Laurentian Trust of Canada Inc.',
    code: '522',
  },
  {
    name: 'M&T Bank',
    code: '357',
  },
  {
    name: 'Manulife Bank of Canada',
    code: '540',
  },
  {
    name: 'Manulife Trust Company',
    code: '626',
  },
  {
    name: 'Maple Bank',
    code: '336',
  },
  {
    name: 'Mega International Commercial Bank (Canada)',
    code: '269',
  },
  {
    name: 'Meridian Credit Union',
    code: '837',
  },
  {
    name: 'Mizuho Corporate Bank Ltd. Canada Branch',
    code: '277',
  },
  {
    name: 'MonCana Bank of Canada',
    code: '361',
  },
  {
    name: 'Montreal Trust Company of Canada',
    code: '550',
  },
  {
    name: 'National Bank of Canada',
    code: '6',
  },
  {
    name: 'National Trust Company',
    code: '590',
  },
  {
    name: 'Ontario Civil Service Credit Union Limited',
    code: '846',
  },
  {
    name: 'Pacific & Western Bank of Canada',
    code: '334',
  },
  {
    name: 'Peace Hills Trust Company',
    code: '568',
  },
  {
    name: 'Peoples Trust Company',
    code: '621',
  },
  {
    name: 'Rabobank Nederland',
    code: '322',
  },
  {
    name: 'ResMor Trust Company',
    code: '620',
  },
  {
    name: 'Royal Bank Mortgage Corporation',
    code: '592',
  },
  {
    name: 'Royal Bank of Canada',
    code: '3',
  },
  {
    name: 'Royal Bank of Scotland N.V. (Canada) Branch',
    code: '240',
  },
  {
    name: 'Royal Trust Company',
    code: '570',
  },
  {
    name: 'Royal Trust Corporation of Canada',
    code: '580',
  },
  {
    name: 'Scotia Mortgage Corporation',
    code: '606',
  },
  {
    name: 'Shinhan Bank Canada',
    code: '355',
  },
  {
    name: 'Société Générale (Canada Branch)',
    code: '292',
  },
  {
    name: 'Société Générale (Canada Branch) Ontario',
    code: '346',
  },
  {
    name: 'State Bank of India (Canada) Alberta',
    code: '294',
  },
  {
    name: 'State Street',
    code: '327',
  },
  {
    name: 'Sumitomo Mitsui Banking Corporation of Canada',
    code: '301',
  },
  {
    name: 'Sun Life Financial Trust Inc.',
    code: '551',
  },
  {
    name: 'TD Mortgage Corporation',
    code: '597',
  },
  {
    name: 'TD Pacific Mortgage Corporation',
    code: '603',
  },
  {
    name: 'The Bank of New York Mellon',
    code: '242',
  },
  {
    name: 'The Bank of Nova Scotia',
    code: '2',
  },
  {
    name: 'The Canada Trust Company',
    code: '509',
  },
  {
    name: 'The Equitable Trust Company',
    code: '623',
  },
  {
    name: 'The Northern Trust Company Canada Branch',
    code: '349',
  },
  {
    name: 'The Toronto-Dominion Bank',
    code: '4',
  },
  {
    name: 'U.S. Bank National Association',
    code: '318',
  },
  {
    name: 'UBS AG Canada Branch',
    code: '339',
  },
  {
    name: 'UBS Bank (Canada)',
    code: '290',
  },
  {
    name: 'United Overseas Bank Limited',
    code: '335',
  },
  {
    name: 'Walmart Canada Bank',
    code: '359',
  },
  {
    name: 'Wealthsimple Investments Inc.',
    code: '703',
  },
];
export const getInstitution = (
  Code: number,
): {
  name: string;
  code: string;
}[] => {
  const bank = canadaBanks.filter((b) => parseInt(b.code, 10) === Code);
  return bank;
};
export const hasBankImage = (BankId: string): boolean => {
  if (validBankImages.indexOf(BankId) !== -1) {
    return true;
  }

  return false;
};
export const NumbersOnly = (input: string): string => input.replace(/\D/g, '');
export const IsTodayDate = (Date: string): boolean => moment().format('YYYY-MM-DD') === Date;

export function scheduleTypeLabel(value: string): string {
  if (value === 'single') {
    return 'One Time';
  }
  if (value === 'recurring') {
    return 'Recurring';
  }
  return value;
}

export function semiMonthlyFrequencyLabel(value: string): string {
  if (value === 'SemiMonthFirstAndFifteenth') {
    return '1st and 15th Day of the Month';
  }
  if (value === 'SemiMonthFifteenthAndLast') {
    return '15th and Last Day of the Month';
  }
  return value;
}

export function frequencyLabel(value: string): string {
  if (value === 'monthly') {
    return 'Monthly';
  }
  if (value === 'weekly') {
    return 'Weekly';
  }
  if (value === 'biweekly' || value === 'biWeekly') {
    return 'Bi-Weekly';
  }
  if (value === '3 months') {
    return 'Quarterly';
  }
  if (value === '6 months') {
    return 'Semi-Annually';
  }
  if (value === 'yearly') {
    return 'Annually';
  }
  if (value === 'semi-monthly') {
    return 'Semi-Monthly';
  }
  if (value === 'bimonthly') {
    return 'Bi-Monthly';
  }
  if (value === 'daily') {
    return 'Daily';
  }
  if (value === 'single') {
    return 'Single';
  }
  return value;
}

export function transferTypeLabel(value: string): string {
  if (value === 'fund-transfer') {
    return 'Fund and Transfer';
  }
  if (value === 'transfer-withdraw') {
    return 'Transfer and Withdraw';
  }
  return 'Fund, Transfer and Withdraw';
}

export const joinItemsComma = (params: string[]): string => {
  const filteredArray = params.filter((item) => item && item !== ''); // Removes empty items

  const result = filteredArray.join(', ');
  return result;
};
export const formattedCurrency = (amount: string): string =>
  new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    useGrouping: true,
  }).format(Number(sanitizeToNumeric(amount)));

export const getCurrencySign = (currency: string): string =>
  currency === 'CAD' || currency === 'USD' || currency === 'AUD'
    ? '$'
    : currency === 'DKK' || currency === 'SEK' || currency === 'NOK'
      ? 'kr'
      : currency === 'GBP'
        ? '£'
        : currency === 'EUR'
          ? '€'
          : currency === 'PLN'
            ? 'zł'
            : '$';

export const createSyntheticEvent = <T extends Element>(name: string, value: unknown): React.ChangeEvent<T> => {
  const event = new Event('change', {
    bubbles: true,
  });
  let isDefaultPrevented = false;
  let isPropagationStopped = false;
  const preventDefault = () => {
    isDefaultPrevented = true;
    event.preventDefault();
  };
  const stopPropagation = () => {
    isPropagationStopped = true;
    event.stopPropagation();
  };
  const newTarget: unknown = {
    ...event.currentTarget,
    name,
    value,
  };
  return {
    nativeEvent: event,
    currentTarget: newTarget as EventTarget & T,
    target: newTarget as EventTarget & T,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    eventPhase: event.eventPhase,
    isTrusted: event.isTrusted,
    preventDefault,
    isDefaultPrevented: () => isDefaultPrevented,
    stopPropagation,
    isPropagationStopped: () => isPropagationStopped,
    persist: () => {},
    timeStamp: event.timeStamp,
    type: event.type,
  };
};
export const createSyntheticFocusEvent = <T extends Element>(name: string, value: unknown): React.FocusEvent<T> => {
  const event = new FocusEvent('focus', {
    bubbles: true,
    cancelable: true,
    relatedTarget: null, // You can assign an actual related target here if necessary
  });

  let isDefaultPrevented = false;
  let isPropagationStopped = false;

  const preventDefault = () => {
    isDefaultPrevented = true;
    event.preventDefault();
  };
  const stopPropagation = () => {
    isPropagationStopped = true;
    event.stopPropagation();
  };

  const newTarget: unknown = {
    ...event.currentTarget,
    name,
    value,
  };

  return {
    nativeEvent: event,
    relatedTarget: event.relatedTarget as (EventTarget & Element) | null, // Ensure relatedTarget is an Element or null
    currentTarget: newTarget as EventTarget & T,
    target: newTarget as EventTarget & T,
    bubbles: event.bubbles,
    cancelable: event.cancelable,
    defaultPrevented: event.defaultPrevented,
    eventPhase: event.eventPhase,
    isTrusted: event.isTrusted,
    preventDefault,
    isDefaultPrevented: () => isDefaultPrevented,
    stopPropagation,
    isPropagationStopped: () => isPropagationStopped,
    persist: () => {},
    timeStamp: event.timeStamp,
    type: event.type,
  };
};

export const ACTCurrenciesIDToCurrency = (ATCCurrincies_ID: string): string => {
  if (ATCCurrincies_ID === '1') {
    return 'USD';
  }

  return 'CAD';
};
export const getRootDomain = (hostname: string): string => {
  const parts = hostname.split('.');
  if (parts.length > 2) {
    return parts.slice(1).join('.'); // Remove the first part (subdomain)
  }
  return hostname; // No subdomain, return as-is
};
export const GetColorStatus = (colorStatus?: string): 'neutral' | 'positive' | 'negative' | 'info' => {
  const status = colorStatus?.toLocaleLowerCase();
  if (
    status === 'pending' ||
    status === 'pending approval' ||
    status === 'in progress' ||
    status === 'waiting' ||
    status === 'upcoming' ||
    status === 'inactive' ||
    status === 'submitted' ||
    status === 'not active' ||
    status === 'unverified' ||
    status === 'uploaded' ||
    status === 'processing'
  ) {
    return 'neutral';
  }
  if (
    status === 'active' ||
    status === 'completed' ||
    status === 'complete' ||
    status === 'successful' ||
    status === 'success' ||
    status === 'settled' ||
    status === 'approved' ||
    status === 'processed' ||
    status === 'solved' ||
    status === 'created' ||
    status === 'verified'
  ) {
    return 'positive';
  }
  if (status === 'sent' || status === 'requested' || status === 'scheduled') {
    return 'info';
  }
  if (
    status === 'declined' ||
    status === 'expired' ||
    status === 'cancelled' ||
    status === 'failed' ||
    status === 'rejected' ||
    status === 'cancellation requested' ||
    status === 'cancelled by user'
  ) {
    return 'negative';
  }
  return 'neutral';
};
export const getLuminance = (color: string): number => {
  let rgbColor = {
    r: 255,
    g: 255,
    b: 255,
  };

  if (color.startsWith('#')) {
    rgbColor = hexToRgbParts(color);
  } else if (color.startsWith('rgb')) {
    rgbColor = rgbToRgbParts(color);
  } else if (color.startsWith('color(srgb')) {
    rgbColor = colorRgbToRgbParts(color);
  }

  // // Remove "#" if present
  // const formattedColor = color.replace('#', '');

  // // Convert color to RGB
  // const r = parseInt(formattedColor.slice(0, 2), 16);
  // const g = parseInt(formattedColor.slice(2, 4), 16);
  // const b = parseInt(formattedColor.slice(4, 6), 16);

  // Calculate relative luminance
  const luminance = (0.2126 * rgbColor.r + 0.7152 * rgbColor.g + 0.0722 * rgbColor.b) / 255;

  return luminance;
};

function colorRgbToRgbParts(color: string) {
  const match = color.match(/([0-9.]+)/g);

  if (match) {
    // these values are between 0 and 1, so we need to normalize to 0-255
    const r = parseFloat(match[0]) * 255;
    const g = parseFloat(match[1]) * 255;
    const b = parseFloat(match[2]) * 255;

    return { r, g, b };
  }

  return { r: 255, g: 255, b: 255 };
}

function hexToRgbParts(color: string) {
  // Remove "#" if present
  const formattedColor = color.replace('#', '');

  // Convert color to RGB
  const r = parseInt(formattedColor.slice(0, 2), 16);
  const g = parseInt(formattedColor.slice(2, 4), 16);
  const b = parseInt(formattedColor.slice(4, 6), 16);

  return { r, g, b };
}

function rgbToRgbParts(color: string) {
  const parts = color.match(/\d+/g)?.map(Number) || [0, 0, 0];

  return { r: parts[0], g: parts[1], b: parts[2] };
}

export const getCSSVariable = (variable: string): string => {
  const root = document.documentElement;
  const computedStyle = getComputedStyle(root);
  const value = computedStyle.getPropertyValue(variable).trim();
  return value;
};

export const setCSSVariable = (variable: string, value: string | null) => {
  document.documentElement.style.setProperty(variable, value);
};

export const getContrastColor = (background: string): string => {
  const luminance = getLuminance(background);
  return luminance > 0.5 ? '#2D2D2D' : '#FBFBFB'; // Use black or white depending on luminance threshold
};

export const chooseColor = <T extends string | null | undefined>(defaultColor: string, ...colors: T[]): string => {
  const nonNullColors = colors.filter((color) => color !== null && color !== undefined && color !== '') as string[];

  if (nonNullColors.length === 0) {
    return defaultColor;
  }

  return nonNullColors[0];
};

export const lightenDarkenColor = (color: string, percentage: number): string => {
  // Remove hash if present
  const hasHash = color.startsWith('#');
  const hexColor = hasHash ? color.substring(1) : color;

  // Ensure the percent is between -100 and 100
  const percent = Math.min(100, Math.max(-100, percentage));

  // Convert color to RGB
  let r = parseInt(hexColor.substring(0, 2), 16);
  let g = parseInt(hexColor.substring(2, 4), 16);
  let b = parseInt(hexColor.substring(4, 6), 16);

  // Adjust each component based on the percentage
  r = Math.max(0, Math.min(255, r + Math.round((percent / 100) * 255)));
  g = Math.max(0, Math.min(255, g + Math.round((percent / 100) * 255)));
  b = Math.max(0, Math.min(255, b + Math.round((percent / 100) * 255)));

  // Convert back to hexadecimal and return the result
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const hexToRgb = (color: string) => {
  // Remove hash if present
  const hasHash = color.startsWith('#');
  const hexColor = hasHash ? color.substring(1) : color;

  // Convert color to RGB
  const r = parseInt(hexColor.substring(0, 2), 16);
  const g = parseInt(hexColor.substring(2, 4), 16);
  const b = parseInt(hexColor.substring(4, 6), 16);

  return `${r}, ${g}, ${b}`;
};

export const getHighlightColor = (color: string): string => {
  const luminance = getLuminance(color);
  return luminance > 0.5 ? 'rgba(45,45,45,0.1)' : 'rgba(251,251,251,0.1)';
};
export const addHashIfNeeded = (hexColor: string): string => {
  // Check if the hexColor already starts with a hash
  if (!hexColor.startsWith('#')) {
    // If not, add a hash to the beginning
    return `#${hexColor}`;
  }

  return hexColor;
};
export const getImageInfo = (imageData: string | File): Promise<{ width: number; height: number; size: number }> =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = function handleLoad() {
      const { width, height } = this as HTMLImageElement;
      const size = imageData instanceof File ? imageData.size : imageData.length;
      resolve({ width, height, size });
    };
    image.onerror = () => {
      reject(new Error('Failed to load image.'));
    };

    if (typeof imageData === 'string') {
      image.src = imageData; // base64 encoded image
    } else if (imageData instanceof File) {
      const reader = new FileReader();
      reader.onload = (event) => {
        image.src = event.target?.result as string;
      };
      reader.onerror = (error) => {
        reject(error);
      };
      reader.readAsDataURL(imageData);
    } else {
      reject(new Error('Invalid image data.'));
    }
  });

const hexColorRegex = /^#[0-9a-fA-F]{6}$/;

export function validateHex(hex: string): boolean {
  return hexColorRegex.test(hex);
}

export function validateAndReturn(hex1: string, hex2: string): string {
  if (!validateHex(hex2)) {
    return hex1;
  }
  return hex2;
}
export function getAvailablePaymentMethodsDropdown(
  options: { hasBankAccount: boolean; hasCreditCard: boolean; hasDebitCard: boolean; hasInterac: boolean },
  country: string,
  PaymentMethod: 'Payable' | 'Receivable',
): { [key: string]: string } {
  let newObject: { [key: string]: string } = {};
  if (options.hasBankAccount) {
    if (country === 'CA' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
      };
    }
    if (country === 'CA' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
        'VoPay Instant': 'VoPay Instant',
      };
    }
    if (country === 'US' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
      };
    }
    if (country === 'US' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Bank Account': 'Bank Account',
        RTP: 'RTP',
      };
    }
  }
  if (options.hasCreditCard) {
    if (PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Credit Card': 'Credit Card',
      };
    }
  }
  if (options.hasDebitCard) {
    newObject = {
      ...newObject,
      'Debit Card': 'Debit Card',
    };
  }
  if (options.hasInterac) {
    if (country === 'CA' && PaymentMethod === 'Receivable') {
      newObject = {
        ...newObject,
        'Interac e-Transfer Money Request': '<i>Interac</i> Money Request<sup>&reg;</sup>',
      };
    }
    if (country === 'CA' && PaymentMethod === 'Payable') {
      newObject = {
        ...newObject,
        'Interac e-Transfer': 'Interac e-Transfer',
      };
    }
  }
  return newObject;
}
export function getPaymentRailsAccountingSoftware(
  PaymentType: string,
  country: string,
  PaymentMethod: 'Payable' | 'Receivable',
): 'eft' | 'interac' | 'ach' | 'creditcard' | 'aft' | 'vopayinstant' | 'rtp' | 'oct' {
  if (PaymentType === 'Bank Account' && country === 'CA') {
    return 'eft';
  }
  if (PaymentType === 'Bank Account' && country === 'US') {
    return 'ach';
  }
  if (PaymentType === 'RTP') {
    return 'rtp';
  }
  if (PaymentType === 'VoPay Instant') {
    return 'vopayinstant';
  }
  if (PaymentType === 'Credit Card') {
    return 'creditcard';
  }
  if (PaymentType === 'Debit Card' && PaymentMethod === 'Payable') {
    return 'oct';
  }
  if (PaymentType === 'Debit Card' && PaymentMethod === 'Receivable') {
    return 'aft';
  }
  if (PaymentType === 'Interac e-Transfer Money Request') {
    return 'interac';
  }
  if (PaymentType === 'Interac e-Transfer') {
    return 'interac';
  }
  return 'eft';
}
export function getPaymentTypeFromPaymentRail(paymentRail: string): string {
  switch (paymentRail) {
    case 'eft':
      return 'Bank Account';
    case 'ach':
      return 'Bank Account';
    case 'rtp':
      return 'RTP';
    case 'vopayinstant':
      return 'VoPay Instant';
    case 'creditcard':
      return 'Credit Card';
    case 'oct':
      return 'Debit Card';
    case 'aft':
      return 'Debit Card';
    case 'interac':
      return 'Interac e-Transfer'; // or 'Interac e-Transfer Money Request'
    default:
      return '';
  }
}

export function createTimestamp(timestamp: number | undefined): Timestamp | undefined {
  if (timestamp === undefined) {
    return undefined;
  }
  return timestamp as Timestamp;
}
export function removeWhiteSpaceURI(Text: string) {
  return decodeURI(Text).replaceAll(' ', '');
}

/**
 * Build up a className or classNames more easily.
 * Conditionally add classes with `bool && 'myConditionalClassName'`
 * Reference the actual className, even if it's snake-case.
 *
 * @param styles - the imported *.module.scss
 * @returns a function that can build up a class list inside of classNames
 *
 * @example
 * ```
 * import styles from './MyComponent.styles.scss'
 *
 * const classes = withStyles(styles);
 *
 * export default function MyComponent() {
 *  const hasError = true;
 *  const arrayOfClasses = ['more', 'classes'];
 *  const isEnabled = false;
 *  const isTitle = false;
 *
 *  return (
 *    <div className={classes(
 *      'base',
 *      undefined,
 *      arrayOfClasses,
 *      hasError && 'bg-red',
 *      isEnabled || 'pointer-events-none',
 *      isTitle ? 'font-semibold' : 'font-normal'
 *    )}>
 *      Result: "base more classes bg-red font-normal"
 *    </div>
 *  )
 * }
 * ```
 */
export function withStyles(styles: Record<string, string>) {
  return (...args: unknown[]) =>
    args
      .flat()
      .filter((x): x is string => typeof x === 'string')
      .map((x) => styles[x] || x)
      .join(' ')
      .trim();
}

export const getCurrencySymbol = (currency: SupportedXBCurrencies) => {
  switch (currency) {
    case 'EUR':
      return '€';
    case 'GBP':
      return '£';
    case 'JPY':
      return '¥';
    case 'CNY':
      return '¥';
    case 'EGP':
      return 'E£';
    case 'BDT':
      return '৳';
    case 'BRL':
      return 'R$';
    case 'DOP':
      return 'RD$';
    case 'GHS':
      return '¢';
    case 'INR':
      return '₹';
    case 'JMD':
      return 'J$';
    case 'KES':
      return 'KES';
    case 'KRW':
      return '₩';
    case 'LKR':
      return '₨';
    case 'PHP':
      return '₱';
    case 'ZAR':
      return 'R';
    case 'THB':
      return '฿';
    case 'VND':
      return '₫';
    case 'AUD':
      return '$';
    case 'CAD':
      return '$';
    case 'HKD':
      return '$';
    case 'IDR':
      return 'Rp';
    case 'MXN':
      return '$';
    case 'USD':
      return '$';
    case 'NZD':
      return '$';
    default:
      return currency;
  }
};
export const getCurrency = (countryCode: SupportedXBCountries): SupportedXBCurrencies => {
  if (
    countryCode === 'AND' ||
    countryCode === 'AUT' ||
    countryCode === 'BEL' ||
    countryCode === 'BGR' ||
    countryCode === 'HRV' ||
    countryCode === 'CYP' ||
    countryCode === 'CZE' ||
    countryCode === 'DNK' ||
    countryCode === 'EST' ||
    countryCode === 'FIN' ||
    countryCode === 'FRA' ||
    countryCode === 'DEU' ||
    countryCode === 'GRC' ||
    countryCode === 'HUN' ||
    countryCode === 'ISL' ||
    countryCode === 'IRL' ||
    countryCode === 'ITA' ||
    countryCode === 'LVA' ||
    countryCode === 'LIE' ||
    countryCode === 'LTU' ||
    countryCode === 'LUX' ||
    countryCode === 'MLT' ||
    countryCode === 'MCO' ||
    countryCode === 'NLD' ||
    countryCode === 'NOR' ||
    countryCode === 'POL' ||
    countryCode === 'PRT' ||
    countryCode === 'ROU' ||
    countryCode === 'SMR' ||
    countryCode === 'SVK' ||
    countryCode === 'SVN' ||
    countryCode === 'ESP' ||
    countryCode === 'SWE' ||
    countryCode === 'CHE' ||
    countryCode === 'VAT'
  ) {
    return 'EUR';
  }
  if (countryCode === 'AUS') {
    return 'AUD';
  }
  if (countryCode === 'BGD') {
    return 'BDT';
  }
  if (countryCode === 'BRA') {
    return 'BRL';
  }
  if (countryCode === 'CAN') {
    return 'CAD';
  }
  if (countryCode === 'CHN') {
    return 'CNY';
  }
  if (countryCode === 'EGY') {
    return 'EGP';
  }
  if (countryCode === 'DOM') {
    return 'DOP';
  }
  if (countryCode === 'GHA') {
    return 'GHS';
  }
  if (countryCode === 'HKG') {
    return 'HKD';
  }
  if (countryCode === 'IDN') {
    return 'IDR';
  }
  if (countryCode === 'IND') {
    return 'INR';
  }
  if (countryCode === 'JAM') {
    return 'JMD';
  }
  if (countryCode === 'JPN') {
    return 'JPY';
  }
  if (countryCode === 'KEN') {
    return 'KES';
  }
  if (countryCode === 'MEX') {
    return 'MXN';
  }
  if (countryCode === 'ZAF') {
    return 'ZAR';
  }
  if (countryCode === 'KOR') {
    return 'KRW';
  }
  if (countryCode === 'LKA') {
    return 'LKR';
  }
  if (countryCode === 'THA') {
    return 'THB';
  }
  if (countryCode === 'GBR') {
    return 'GBP';
  }
  if (countryCode === 'USA') {
    return 'USD';
  }
  if (countryCode === 'VNM') {
    return 'VND';
  }
  if (countryCode === 'PHL') {
    return 'PHP';
  }

  return '';
};
export const getCountryName = (code: string): string => {
  const codeToCountryMap: Record<string, string> = {
    AND: 'Andorra',
    AUT: 'Austria',
    BEL: 'Belgium',
    BGR: 'Bulgaria',
    HRV: 'Croatia',
    CYP: 'Cyprus',
    CZE: 'Czechia',
    DNK: 'Denmark',
    EST: 'Estonia',
    FIN: 'Finland',
    FRA: 'France',
    DEU: 'Germany',
    GRC: 'Greece',
    HUN: 'Hungary',
    ISL: 'Iceland',
    IRL: 'Ireland',
    ITA: 'Italy',
    LVA: 'Latvia',
    LIE: 'Liechtenstein',
    LTU: 'Lithuania',
    LUX: 'Luxembourg',
    MLT: 'Malta',
    MCO: 'Monaco',
    NLD: 'Netherlands',
    NOR: 'Norway',
    POL: 'Poland',
    PRT: 'Portugal',
    ROU: 'Romania',
    SMR: 'San Marino',
    SVK: 'Slovakia',
    SVN: 'Slovenia',
    ESP: 'Spain',
    SWE: 'Sweden',
    CHE: 'Switzerland',
    VAT: 'Vatican City',
    AUS: 'Australia',
    BGD: 'Bangladesh',
    BRA: 'Brazil',
    CAN: 'Canada',
    CHN: 'China',
    EGY: 'Egypt',
    DOM: 'Dominican Republic',
    GHA: 'Ghana',
    HKG: 'Hong Kong',
    IDN: 'Indonesia',
    IND: 'India',
    JAM: 'Jamaica',
    JPN: 'Japan',
    KEN: 'Kenya',
    MEX: 'Mexico',
    ZAF: 'South Africa',
    KOR: 'South Korea',
    LKA: 'Sri Lanka',
    THA: 'Thailand',
    GBR: 'United Kingdom',
    USA: 'United States',
    VNM: 'Vietnam',
    PHL: 'Philippines',
    '': '',
  };

  return codeToCountryMap[code] || code;
};
export const getCountryCode = (country: string): SupportedXBCountries => {
  const countryToCodeMap: Record<string, SupportedXBCountries> = {
    Andorra: 'AND',
    Austria: 'AUT',
    Belgium: 'BEL',
    Bulgaria: 'BGR',
    Croatia: 'HRV',
    Cyprus: 'CYP',
    Czechia: 'CZE',
    CzechRepublic: 'CZE',
    Denmark: 'DNK',
    Estonia: 'EST',
    Finland: 'FIN',
    France: 'FRA',
    Germany: 'DEU',
    Greece: 'GRC',
    Hungary: 'HUN',
    Iceland: 'ISL',
    Ireland: 'IRL',
    Italy: 'ITA',
    Latvia: 'LVA',
    Liechtenstein: 'LIE',
    Lithuania: 'LTU',
    Luxembourg: 'LUX',
    Malta: 'MLT',
    Monaco: 'MCO',
    Netherlands: 'NLD',
    Norway: 'NOR',
    Poland: 'POL',
    Portugal: 'PRT',
    Romania: 'ROU',
    SanMarino: 'SMR',
    Slovakia: 'SVK',
    Slovenia: 'SVN',
    Spain: 'ESP',
    Sweden: 'SWE',
    Switzerland: 'CHE',
    VaticanCity: 'VAT',
    Vatican: 'VAT',
    Australia: 'AUS',
    Bangladesh: 'BGD',
    Brazil: 'BRA',
    Canada: 'CAN',
    China: 'CHN',
    Egypt: 'EGY',
    DominicanRepublic: 'DOM',
    Ghana: 'GHA',
    HongKong: 'HKG',
    Indonesia: 'IDN',
    India: 'IND',
    Jamaica: 'JAM',
    Japan: 'JPN',
    Kenya: 'KEN',
    Mexico: 'MEX',
    SouthAfrica: 'ZAF',
    SouthKorea: 'KOR',
    SriLanka: 'LKA',
    Thailand: 'THA',
    UnitedKingdom: 'GBR',
    UnitedStates: 'USA',
    Vietnam: 'VNM',
    Philippines: 'PHL',
  };

  if (Object.values(countryToCodeMap).includes(country as SupportedXBCountries)) {
    return country as SupportedXBCountries;
  }

  return countryToCodeMap[country.replaceAll(' ', '')];
};

export const getCallingCode = (countrycode: SupportedXBCountries) => {
  const callingCodes = {
    AUS: '61',
    BGD: '880',
    BRA: '55',
    CAN: '1',
    CHN: '86',
    DOM: '1',
    GHA: '233',
    HKG: '852',
    IND: '91',
    JAM: '1',
    JPN: '81',
    KEN: '254',
    KOR: '82',
    MEX: '52',
    PHL: '63',
    AND: '376',
    AUT: '43',
    BEL: '32',
    BGR: '359',
    HRV: '385',
    CYP: '357',
    CZE: '420',
    DNK: '45',
    EST: '372',
    FIN: '358',
    FRA: '33',
    DEU: '49',
    GRC: '30',
    HUN: '36',
    IRL: '353',
    ISL: '354',
    ITA: '39',
    LVA: '371',
    LIE: '423',
    LTU: '370',
    LUX: '352',
    MLT: '356',
    MCO: '377',
    NLD: '31',
    NOR: '47',
    POL: '48',
    PRT: '351',
    ROU: '40',
    SMR: '378',
    SVK: '421',
    SVN: '386',
    ESP: '34',
    SWE: '46',
    CHE: '41',
    GBR: '44',
    VAT: '379',
    ZAF: '27',
    LKA: '94',
    THA: '66',
    USA: '1',
    VNM: '84',
    EGY: '20',
    IDN: '62',
    '': '',
  };
  return callingCodes[countrycode];
};

export function loadScriptByURL(id: string, url: string, attrs?: Record<string, string>) {
  return new Promise<void>((resolve, reject) => {
    const doesScriptExist = document.getElementById(id);

    // If the script has not been loaded, load it
    if (!doesScriptExist) {
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.src = url;
      script.id = id;

      if (attrs) {
        Object.entries(attrs).forEach(([key, value]) => {
          script.setAttribute(key, value);
        });
      }

      script.onload = () => {
        resolve();
      };

      script.onerror = (error) => {
        reject(error);
      };

      document.body.appendChild(script);
    } else {
      resolve();
    }
  });
}

export function countryToISO(countryName: string): string {
  const isoMapping: { [key: string]: string } = {
    'united states': 'US',
    canada: 'CA',
  };

  // Convert the input to lowercase for a case-insensitive match
  const normalizedCountry = countryName.toLowerCase();

  // Return ISO code if found, otherwise return the original input
  return isoMapping[normalizedCountry] || countryName;
}

export function convertToOptions(options: { [key: string]: string }): { label: string; value: string }[] {
  return Object.entries(options).map(([key, value]) => ({
    label: value,
    value: key,
  }));
}
export function formatDate(date: string, pattern = 'yyyy-MM-dd') {
  return format(date, pattern);
}

export const useUpdateQueryParams = <T extends Record<string, string | null>>() => {
  const history = useHistory();
  const location = useLocation();

  const updateQueryParams = (newParams: Partial<T>) => {
    const searchParams = new URLSearchParams(location.search);

    Object.keys(newParams).forEach((key) => {
      const typedKey = key;
      const paramValue = newParams[typedKey];

      if (paramValue !== null && paramValue !== undefined && paramValue !== '') {
        searchParams.set(typedKey, paramValue.toString());
      } else {
        searchParams.delete(typedKey);
      }
    });

    history.replace({
      search: searchParams.toString(),
    });
  };

  return updateQueryParams;
};
export const setTitle = (title: string): void => {
  document.title = title;
};

export const setFavicon = (faviconUrl: string): void => {
  let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }
  link.href = faviconUrl;
};

export function toPascalCase(value: string): string {
  return value
    .split(' ')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

export function formatAmount(value: number | string) {
  const number = typeof value === 'string' ? parseFloat(value) : value;

  if (Number.isNaN(number) || number === undefined) return '0.00';

  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(number);
}

export function formatPercent(rate: number) {
  return new Intl.NumberFormat(undefined, {
    style: 'percent',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rate);
}

export const convertToNumber = (value: string): number | undefined => {
  const sanitizedValue = sanitizeToNumeric(value);
  if (sanitizedValue) {
    return Number(sanitizedValue);
  }
  return undefined;
};
export function formatPhoneNumber(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check the length of the cleaned number
  if (cleaned.length < 10 || cleaned.length > 12) {
    // If the length is invalid, return the original input
    return phoneNumber;
  }

  let countryCode = '1'; // Default country code
  let localNumber = cleaned;

  if (cleaned.length === 11) {
    // If the length is 11, assume the first digit is the country code

    countryCode = cleaned[0];
    localNumber = cleaned.slice(1);
  } else if (cleaned.length === 12) {
    // If the length is 12, assume the first two digits are the country code
    countryCode = cleaned.slice(0, 2);
    localNumber = cleaned.slice(2);
  }

  // Format the local number
  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);
  if (match) {
    return `(+${countryCode}) ${match[1]}-${match[2]}-${match[3]}`;
  }

  return phoneNumber;
}

export function formatPhoneNumberWithParentheses(phoneNumber: string): string {
  // Remove all non-numeric characters
  const cleaned = `${phoneNumber}`.replace(/\D/g, '');

  // Check the length of the cleaned number
  if (cleaned.length < 10 || cleaned.length > 12) {
    // If the length is invalid, return the original input
    return phoneNumber;
  }

  let countryCode = '1'; // Default country code
  let localNumber = cleaned;

  if (cleaned.length === 11) {
    // If the length is 11, assume the first digit is the country code

    countryCode = cleaned[0];
    localNumber = cleaned.slice(1);
  } else if (cleaned.length === 12) {
    // If the length is 12, assume the first two digits are the country code
    countryCode = cleaned.slice(0, 2);
    localNumber = cleaned.slice(2);
  }

  // Format the local number
  const match = /^(\d{3})(\d{3})(\d{4})$/.exec(localNumber);
  if (match) {
    return `+${countryCode} (${match[1]}) ${match[2]}-${match[3]}`;
  }

  return phoneNumber;
}

/** Preserves types when calling Object.entries; skips `undefined` values so the narrowed value type is true even for explicitly-`undefined` props. */
export function objectEntries<T extends Record<string | number, unknown>>(
  obj?: T | undefined,
): [Extract<keyof T, string | number>, Exclude<T[Extract<keyof T, string | number>], undefined>][] {
  if (!obj) return [];
  return Object.entries(obj).filter(([, value]) => value !== undefined) as [
    Extract<keyof T, string | number>,
    Exclude<T[Extract<keyof T, string | number>], undefined>,
  ][];
}

// TODO: put this in useStorage
export function getMfaHash(email: string | null) {
  if (!email) return;

  return localStorage.getItem(`${LS_MFA_HASH}__${email.toLowerCase()}`);
}

export function storeMfaHash(email: string, hash: string) {
  localStorage.setItem(`${LS_MFA_HASH}__${email.toLowerCase()}`, hash);
}

export function clearMfaHash(email: string) {
  localStorage.removeItem(`${LS_MFA_HASH}__${email.toLowerCase()}`);
}

export function storeRememberMfa(email: string) {
  localStorage.setItem(`${LS_REMEMBER_MFA}__${email.toLowerCase()}`, 'true');
}

export function getRememberMfa(email: string) {
  return localStorage.getItem(`${LS_REMEMBER_MFA}__${email.toLowerCase()}`) === 'true' ? true : false;
}

export function isRecipientClientAccountIDSplit(arr: unknown): arr is RecipientClientAccountIDSplit[] {
  return (
    arr instanceof Array &&
    arr.every((obj) => {
      return (
        typeof obj === 'object' &&
        obj !== null &&
        'ClientAccountID' in obj &&
        typeof obj.ClientAccountID === 'string' &&
        (('Amount' in obj && typeof obj.Amount === 'string') ||
          ('Percentage' in obj && typeof obj.Percentage === 'string'))
      );
    })
  );
}
/**
 * type-checks to make sure you've gotten all of the variants. ts will squiggle out if you've missed a type.
 */
export function ensureAllCasesHandled(value: never): never {
  throw new Error(`Unhandled type: ${value}`);
}
/**
 * Builds a readonly array that is compile-time guaranteed to contain **every** member of a union
 * type `T`. Use it to keep a runtime list in sync with a union: omit a member and TS errors, add a
 * value outside the union and TS errors.
 *
 * Called in two steps — first fix the union with `arrayOfAll<MyUnion>()`, then pass the values:
 *
 * ```ts
 * type Fruit = 'apple' | 'banana' | 'cherry';
 * const FRUITS = arrayOfAll<Fruit>()(['apple', 'banana', 'cherry']); // ok
 * arrayOfAll<Fruit>()(['apple', 'banana']);                          // error: missing 'cherry'
 * ```
 *
 * The `U & (... ? unknown : never)` intersection is the enforcement: when a member is missing the
 * conditional resolves to `never`, so the argument becomes unassignable and the call fails.
 */
export const arrayOfAll =
  <T>() =>
  <U extends readonly T[]>(arr: U & ([T] extends [U[number]] ? unknown : never)) =>
    arr;

const SUPPORTED_CURRENCIES = arrayOfAll<SupportedCurrencies>()([
  'CAD',
  'USD',
  'GBP',
  'EUR',
  'AUD',
  'DKK',
  'SEK',
  'NOK',
  'PLN',
]);

const isSupportedCurrency = (currency: string): currency is SupportedCurrencies =>
  SUPPORTED_CURRENCIES.some((c) => c === currency);

export const toSupportedCurrency = (
  currency: string | undefined,
  fallback: SupportedCurrencies = 'CAD',
): SupportedCurrencies => (currency && isSupportedCurrency(currency) ? currency : fallback);
const isPseudoBool = (value: string): value is PseudoBool => value === '0' || value === '1';

export const toPseudoBool = (value: string | null | undefined, fallback: PseudoBool = '0'): PseudoBool =>
  value && isPseudoBool(value) ? value : fallback;
